<template>
  <div class="fob-public-view">
    <!-- LEFT HAND SIDE | Organisational unit tree and person search -->
    <div class="columns" style="margin-left: 0; margin-right: 0;">
      <div v-if="!isCompactView" class="column fob-hide-in-print" style="max-width: 400px;">
        <!-- SWITCH | Toggle between persons and organisational unit -->
        <div class="level fob-hide-in-print" v-if="!isCompactView" style="padding-top: 5px;">
          <div class="field">
            <b-switch v-model="showOrgaTree" type="is-switch-grey" size="is-small">
              <translate>Public.switch.ou.person</translate>
              <translate
                class="is-italic"
                :class="{ 'has-text-weight-bold': !showOrgaTree }"
              >
                Person
              </translate>
              <translate>und</translate>
              <translate
                class="is-italic"
                :class="{ 'has-text-weight-bold': showOrgaTree }"
              >
                Organisationseinheit
              </translate>
            </b-switch>
          </div>
        </div>
        <fob-public-select :type="showOrgaTree ? 'ou' : 'person'"></fob-public-select>
      </div>
      <!-- RESEARCH ACTIVITY DETAILS OF PERSON OR ORGANISATIONAL UNIT -->
      <div class="column">
        <fob-public-list-view-header>
          <!-- HEADING -->
          <template slot="heading">
            {{ label }}
          </template>
          <!-- IF ORGANISATIONAL UNIT ... -->
          <template v-if="rootQueryType === 'organisationalUnit'">
            <template slot="subheading">
              <div v-if="getOrgaDuration(orgaDetails)" >
                {{ getOrgaDuration(orgaDetails) }}
              </div>
            </template>
            <template slot="description">
              <div
                v-if="orgaDescription"
                v-html="orgaDescription[currentLocale]"
              ></div>
            </template>
          </template>
          <!-- IF PERSON ... -->
          <template v-if="rootQueryType === 'person'">
            <template slot="subheading">
              <template v-if="leurisProfile && orcid">
                <div class="is-size-6">
                  <a href="https://orcid.org" target="_blank">
                    <img
                      width="16"
                      height="16"
                      hspace="4"
                      vspace="3"
                      src="../assets/orcid_logo.svg"
                    >
                  </a>
                  <a :href="orcidLink" target="_blank">
                    {{ orcidLink }}
                  </a>
                </div>
              </template>
              <div v-if="ulWebsiteProfile[currentLocale]"
                style="margin: 4px 0 8px 4px;"
              >
                <a :href="ulWebsiteProfile[currentLocale]" target="_blank">
                  <b-icon icon="sign-in" size="is-small" style="margin-right: 4px"></b-icon>
                  <translate>UL Profile Link</translate>
                </a>
              </div>
            </template>
            <template slot="description">
              <div
                v-if="leurisProfile || leurisProfileFisPersid"
              >
                <!-- If a profile is available, display the possiblity empty abstract -->
                <template v-if="leurisProfile">
                  <div v-html="leurisProfile.abstract[currentLocale]"></div>
                </template>


                <!-- "No text available" and edit button only for user themself" -->
                <template v-if="userIsLoggedIn && (leurisProfileFisPersid === userFisPersid)">
                  <div v-if="leurisProfile == null || (leurisProfile && !leurisProfile.abstract[currentLocale])">
                    <em><translate>LeurisProfile.abstract.empty</translate></em>
                  </div>
                  <div style="margin: 1rem 0 1rem 0;">
                    <b-icon icon="pencil" style="margin-right: 0.25rem;" size="is-small"></b-icon>
                    <a :href="`${leurisProfileBaseUrl}/${leurisProfileFisPersid}/drafts/abstract`"><translate>LeurisProfile.abstract.edit</translate></a>
                  </div>
                </template>
              </div>
            </template>
          </template>
          <!-- QUICK ACTIONS -->
          <template slot="quick-actions">
            <fob-public-list-quick-actions
              :qa-share="qaShareSettings"
              :qa-print="true"
              :qa-export="true"
            ></fob-public-list-quick-actions>
          </template>
        </fob-public-list-view-header>

        <div>
          <span
            class="fob-only-for-print-view is-size-7"
            style="margin-top: 20px;"
          ><translate>{{activeResource.label}}</translate></span>
        </div>

        <!-- RESEARCH ACTIVITY | Tabs -->
        <div class="card">
          <b-tabs v-model="activeTab">
            <b-tab-item
              v-for="resourceType in resourceTypes"
              :key="resourceType.type"
              :disabled="resourceType.disabled"
              style="flex: 0 0 auto;"
            >
              <!-- RESEARCH ACTIVITY | Tabs | Label -->
              <template slot="header">
                <span :class="{'fob-is-filtered': hasActiveFilter(resourceType.type)}">
                  <translate>{{resourceType.label}}</translate>
                  <span>{{`(${total[resourceType.type]})`}}</span>
                </span>
              </template>
              <!-- RESEARCH ACTIVITY | Tabs | Content -->
              <div v-if="!resourceType.disabled">
                <fob-public-list-view :resource-type="resourceType.type" style="height: 100%;"></fob-public-list-view>
              </div>
            </b-tab-item>
          </b-tabs>
        </div>
      </div>
    </div>
    <!-- PRINT FOOTER -->
    <div class="print-footer">
      <span>
        <translate :translate-params="{printDate: printDate}">details.printedOn</translate>
      </span>
    </div>
    <!-- BACK TO TOP | @see https://github.com/caiofsouza/vue-backtotop -->
    <!--
    <back-to-top bottom="50px" right="50px">
      <button
        class="button is-primary fob-hide-in-print"
        style="height: 50px; width: 50px;border-radius: 50%; opacity: 0.8;"
      >
        <b-icon pack="fa" icon="chevron-up" style="height: auto; width: auto;"></b-icon>
      </button>
    </back-to-top>
    -->
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import { currentDateString } from '@/utils'
// import BackToTop from 'vue-backtotop'
import DateTimeHelper from '@/components/mixins/DateTimeHelper'
import OrgaDurationHelper from '@/components/mixins/OrgaDurationHelper'
import DynamicTitle from '@/components/mixins/DynamicTitle'
import EntityHeader from '@/components/EntityHeader'
import PublicSelect from '@/components/public/PublicSelect'
import PublicListView from '@/components/public/PublicListView'
import PublicListViewHeader from '@/components/public/PublicListViewHeader'
import PublicListQuickActions from '@/components/public/PublicListQuickActions'

export default {
  name: 'fob-public',
  mixins: [DynamicTitle, DateTimeHelper, OrgaDurationHelper],
  data () {
    return {
      printDate: currentDateString(),
      leurisHead: {
        inner: 'leuris:Portal',
        complement: 'menu.list.public'
      }
    }
  },
  computed: {
    ...mapGetters({
      filter: 'publicView/getFilter',
      isLoading: 'publicView/isLoading',
      label: 'publicView/getLabel',
      leurisProfile: 'publicView/getLeurisProfile',
      leurisProfileFisPersid: 'publicView/getLeurisProfileFisPersid',
      orcid: 'publicView/getORCID',
      ulWebsiteProfile: 'publicView/getUlWebsiteProfile',
      sortOrder: 'publicView/getSortOrder',
      sortField: 'publicView/getSortField',
      resourceTypes: 'publicView/resourceTypes',
      selected: 'publicView/getSelected',
      selectedOrganisationalUnit: 'tree/selected',
      total: 'publicView/getTotal',
      userIsLoggedIn: 'user/isAuthorized',
      userFisPersid: 'user/fisPersid',
      orgaDescription: 'publicView/getOuDescription',
      orgaDetails: 'publicView/getOuDetails',
      rootQueryType: 'publicView/getRootQueryType'
    }),
    orcidLink () {
      return `${process.env.ORCID_BASE_URL}/${this.orcid}`
    },
    activeTab: {
      get () {
        return this.$store.getters['publicView/getActiveTab']
      },
      set (activeTab) {
        let params = this.$router.currentRoute.params
        let query = this.$router.currentRoute.query
        this.$store.dispatch('publicView/setActiveTab', activeTab).then(() => {
          this.resourceTypes.forEach((resourceType, idx) => {
            if (activeTab === idx) {
              params['resourceType'] = resourceType.type
              this.$router.replace({name: 'public', params, query})
            }
          })
        })
      }
    },
    leurisProfileBaseUrl () {
      return `/profilseite`
    },
    showOrgaTree: {
      get () {
        return this.$store.getters['publicView/getShowOrgaTree']
      },
      set (show) {
        // show persons
        return this.$store.dispatch('publicView/setShowOrgaTree', show)
      }
    },
    activeResource () {
      return this.resourceTypes[this.activeTab]
    },
    isCompactView () {
      if (this.$route.meta.compact || this.$route.query.compact === true || this.$route.query.compact === 'true') {
        return true
      } else if (this.$route.query.compact === null) {
        return false
      } else {
        return false
      }
    },
    qaShareSettings () {
      return {
        compact: true,
        queryParams: {
          ...this.filter[this.activeResource.type].filter,
          sortOrder: this.sortOrder,
          sortField: this.sortField,
          lang: this.currentLocale
        }
      }
    }
  },
  methods: {
    hasActiveFilter (resourceType) {
      let active = false
      if (Object.keys(this.filter[resourceType].filter).length > 0) {
        Object.keys(this.filter[resourceType].filter).some(filterKey => {
          let value = this.filter[resourceType].filter[filterKey]
          if (value && value.length > 0) {
            active = true
          }
        })
      }
      return active
    },
    setRoute (type, id) {
      let to = {
        name: 'public',
        params: {
          type,
          id,
          resourceType: this.activeResource.type
        }
      }

      if (this.isCompactView) {
        to.query = {
          compact: true
        }
      }
      this.$router.replace(to)
    }
  },
  mounted () {
    setInterval(() => {
      this.printDate = currentDateString()
    }, 30 * 1000)
  },
  components: {
    'fob-entity-header': EntityHeader,
    'fob-public-select': PublicSelect,
    'fob-public-list-view': PublicListView,
    'fob-public-list-view-header': PublicListViewHeader,
    'fob-public-list-quick-actions': PublicListQuickActions
    // BackToTop
  }
}

</script>

<style lang="scss">
  .vue-back-to-top {
    z-index: 32 !important;
  }

  .fob-public-select-divider {
    width: 350px;
    margin: 1rem 0;
    height: 1px;
    background-image: linear-gradient(
      to right,
      rgba(135, 194, 209, 0.1),
      rgba(138, 193, 208, 0.8),
      rgba(138, 194, 209, 0.1)
    );
  }

  .fob-is-filtered {
    font-style: italic;
  }

  .fob-is-filtered:after {
    content: '*'
  }
</style>



// WEBPACK FOOTER //
// src/components/Public.vue